// Zona horaria de la persona (id IANA), guardada en este navegador. Por defecto, la del navegador.
// La usan los formatos de fecha y hora (lib/format) para mostrar todo en la zona elegida.
import { useSyncExternalStore } from 'react';

import { readJSON, writeJSON } from './storage';

export const CLAVE_ZONA = 'consola.zona-horaria';
export const ZONA_REFERENCIA = 'America/Bogota';

export type InfoZona = { id: string; ciudad: string; pais: string; cod: string | null; lat: number | null; lon: number | null };

// Zonas que se ofrecen en Mi perfil, de oeste a este (con su ciudad y pais para el globo).
export const ZONAS: InfoZona[] = [
  { id: 'America/Los_Angeles', ciudad: 'Los Ángeles', pais: 'Estados Unidos', cod: 'US', lat: 34.05, lon: -118.24 },
  { id: 'America/Mexico_City', ciudad: 'Ciudad de México', pais: 'México', cod: 'MX', lat: 19.43, lon: -99.13 },
  { id: 'America/Bogota', ciudad: 'Bogotá', pais: 'Colombia', cod: 'CO', lat: 4.71, lon: -74.07 },
  { id: 'America/Lima', ciudad: 'Lima', pais: 'Perú', cod: 'PE', lat: -12.05, lon: -77.04 },
  { id: 'America/Caracas', ciudad: 'Caracas', pais: 'Venezuela', cod: 'VE', lat: 10.48, lon: -66.9 },
  { id: 'America/Santiago', ciudad: 'Santiago', pais: 'Chile', cod: 'CL', lat: -33.45, lon: -70.67 },
  { id: 'America/Argentina/Buenos_Aires', ciudad: 'Buenos Aires', pais: 'Argentina', cod: 'AR', lat: -34.6, lon: -58.38 },
  { id: 'Europe/London', ciudad: 'Londres', pais: 'Reino Unido', cod: 'GB', lat: 51.51, lon: -0.13 },
  { id: 'Europe/Madrid', ciudad: 'Madrid', pais: 'España', cod: 'ES', lat: 40.42, lon: -3.7 },
  { id: 'Europe/Moscow', ciudad: 'Moscú', pais: 'Rusia', cod: 'RU', lat: 55.76, lon: 37.62 },
  { id: 'Asia/Shanghai', ciudad: 'Shanghái', pais: 'China', cod: 'CN', lat: 31.23, lon: 121.47 },
  { id: 'Asia/Tokyo', ciudad: 'Tokio', pais: 'Japón', cod: 'JP', lat: 35.68, lon: 139.69 },
];

export function zonaValida(z: unknown): z is string {
  if (typeof z !== 'string' || !z) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: z });
    return true;
  } catch {
    return false;
  }
}

export function zonaDelNavegador() {
  try {
    const z = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zonaValida(z) ? z : ZONA_REFERENCIA;
  } catch {
    return ZONA_REFERENCIA;
  }
}

const leer = () => {
  const g = readJSON<{ zona?: string }>(CLAVE_ZONA, {});
  return zonaValida(g.zona) ? g.zona : zonaDelNavegador();
};

let zona = leer();
const subs = new Set<() => void>();

export const zonaActual = () => zona;

export function setZona(z: string) {
  if (!zonaValida(z)) return;
  zona = z;
  writeJSON(CLAVE_ZONA, { zona: z });
  subs.forEach((s) => s());
}

export function suscribirZona(cb: () => void) {
  subs.add(cb);
  return () => {
    subs.delete(cb);
  };
}

// Otra pestana cambio la zona: seguirla.
if (typeof window !== 'undefined')
  window.addEventListener('storage', (e) => {
    if (e.key !== CLAVE_ZONA) return;
    const z = leer();
    if (z === zona) return;
    zona = z;
    subs.forEach((s) => s());
  });

export const useZonaHoraria = () => useSyncExternalStore(suscribirZona, zonaActual, zonaActual);

// Ciudad, pais y coordenadas de una zona; si no esta en la lista, se arma con su id.
export function infoZona(id: string): InfoZona {
  const z = ZONAS.find((x) => x.id === id);
  if (z) return z;
  const ciudad = (id.split('/').pop() ?? id).replace(/_/g, ' ');
  return { id, ciudad, pais: '', cod: null, lat: null, lon: null };
}

const fmtPartes = new Map<string, Intl.DateTimeFormat>();
function formateador(z: string) {
  let f = fmtPartes.get(z);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: z, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    fmtPartes.set(z, f);
  }
  return f;
}

// Fecha y hora de pared en esa zona.
export function partesEn(fecha: Date, z = zona) {
  const p: Record<string, number> = {};
  for (const x of formateador(z).formatToParts(fecha)) if (x.type !== 'literal') p[x.type] = Number(x.value);
  return { anio: p.year, mes: p.month, dia: p.day, hora: p.hour % 24, minuto: p.minute, segundo: p.second };
}

const dos = (n: number) => String(n).padStart(2, '0');

// "2026-10-26" del dia de esa fecha en la zona.
export function claveDia(fecha: Date, z = zona) {
  const p = partesEn(fecha, z);
  return `${p.anio}-${dos(p.mes)}-${dos(p.dia)}`;
}

// Suma dias a una clave "AAAA-MM-DD" (calendario, sin horas de por medio).
export function sumarDias(clave: string, n: number) {
  const [a, m, d] = clave.split('-').map(Number);
  const t = new Date(Date.UTC(a, m - 1, d + n));
  return `${t.getUTCFullYear()}-${dos(t.getUTCMonth() + 1)}-${dos(t.getUTCDate())}`;
}

// Minutos que la zona va adelante (+) o atras (-) de UTC en ese momento.
export function desfaseMin(z: string, fecha = new Date()) {
  const p = partesEn(fecha, z);
  const pared = Date.UTC(p.anio, p.mes - 1, p.dia, p.hora, p.minuto);
  const real = Math.floor(fecha.getTime() / 60_000) * 60_000;
  return Math.round((pared - real) / 60_000);
}

// -300 -> "UTC−5", 330 -> "UTC+5:30", 0 -> "UTC+0".
export function etiquetaUTC(min: number) {
  const s = min < 0 ? '−' : '+';
  const a = Math.abs(min);
  return `UTC${s}${Math.floor(a / 60)}${a % 60 ? `:${dos(a % 60)}` : ''}`;
}

// "2 h más que Bogotá", "1 h 30 min menos que Bogotá" o "Misma hora que Bogotá".
export function diferenciaCon(z: string, ref = ZONA_REFERENCIA, fecha = new Date()) {
  const dif = desfaseMin(z, fecha) - desfaseMin(ref, fecha);
  const nombre = infoZona(ref).ciudad;
  if (!dif) return `Misma hora que ${nombre}`;
  const a = Math.abs(dif);
  const txt = `${Math.floor(a / 60) ? `${Math.floor(a / 60)} h` : ''}${a % 60 ? `${a >= 60 ? ' ' : ''}${a % 60} min` : ''}`;
  return `${txt} ${dif > 0 ? 'más' : 'menos'} que ${nombre}`;
}

// Hora "14:05" en esa zona.
export function horaEn(fecha: Date, z = zona) {
  const p = partesEn(fecha, z);
  return `${dos(p.hora)}:${dos(p.minuto)}`;
}
